import _, { isNaN } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ContextType, ContextTypeReverse } from 'api/conversations';
import {
  ProjectConversation,
  ProjectConversationsAPI,
  ProjectGlobalConversation,
} from 'api/project-conversations';
import {
  pinScopeTypeFromProjectConversation,
  scopeIdFromProjectConversation,
} from 'components/organisms/projects/project-conversation/spotlight-utils';
import { getConversationsKey } from 'hooks/use-conversations';
import { useIsResponsive } from 'hooks/use-media-query';
import { queryClient } from 'queries';
import { useProjectGlobalConversationQuery } from 'queries/project-global-conversation/project-global-conversation';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { CpChatPageWidgetWrapper } from './chat-components';
import { CpChatForLaptop } from './cp-chat.laptop';
import { CpChatForMobile } from './cp-chat.mobile';
import { CpCreateNewConversationModal } from './cp-create-new-conv-modal';
import { CpChatActiveContext, CpChatRenderInput } from './types';

export const CpChatWidget: React.FC<CpWidgetProps> = ({
  widget,
  disabled,
  data: _data,
  onRuntimeConfigChange: onFilterChange,
}) => {
  const data = useMemo(() => _data as CpChatRenderInput, [_data]);

  // The hash should be of format #<projectId?:<entityType>:<entityId>
  // Example: #1:TASK:1
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();

  const isResponsive = useIsResponsive();

  const trySettingInitialCOnv = useRef(false);
  const activeContext = useMemo<CpChatActiveContext>(() => {
    // If the hash is not set, return undefined
    const hash = hashMap?.chat;

    if (!hash) {
      return undefined;
    }

    const hashParts = hash.split(':');

    if (hashParts.length < 2) {
      return undefined;
    }

    const projectId = parseInt(hashParts[0].replace('#', ''), 10);
    const entityType =
      hashParts[1]?.toUpperCase?.() as keyof typeof ContextType;
    const entityId = parseInt(hashParts[2], 10);

    if (
      isNaN(projectId) ||
      isNaN(entityId) ||
      Object.keys(ContextType).indexOf(entityType) === -1
    ) {
      return undefined;
    }

    if (entityType === 'PROJECT_CONVERSATIONS') {
      const allChats =
        data.projectChats?.flatMap((e) => e?.projectConversations) ?? [];
      const chat = allChats.find((e) => e?.conversationId === entityId);

      if (chat) {
        return {
          id: entityId,
          projectId,
          type: entityType,
          name: chat.conversationName,
          desc: chat.conversationDescription,
        };
      }

      return undefined;
    }
    const allEntityChats =
      data.projectChats?.flatMap((e) =>
        e?.entityConversations?.map((x) => ({
          ...x,
          projectId: e?.project?.projectId,
        })),
      ) ?? [];
    const chat = allEntityChats.find((e) => e?.entityId === entityId);

    if (chat) {
      return {
        id: entityId,
        projectId,
        type: entityType,
        name: chat.entityName,
        pinScopeType: pinScopeTypeFromProjectConversation(chat),
        pinScopeId: scopeIdFromProjectConversation(chat),
      };
    }

    return undefined;
  }, [data.projectChats, hashMap?.chat]);

  const setActiveContext = useCallback(
    (
      context:
        | {
            id: number;
            projectId: number;
            type: keyof typeof ContextType;
          }
        | undefined,
    ) => {
      if (context) {
        setHashMap(
          'chat',
          `${context.projectId}:${context.type}:${context.id}`,
        );
      } else {
        removeKey('chat');
      }
    },
    [removeKey, setHashMap],
  );

  const [createNewConversationInProject, setCreateNewConversationInProject] =
    useState<
      | {
          projectId: number;
          name: string;
          desc?: string;
          entityId?: number;
          entityType?: keyof typeof ContextType;
        }
      | undefined
    >();

  const background: string = useMemo(() => {
    const bg = widget?.widgetDefinition?.styles?.background;

    if (!bg) {
      return '#F0F0F0';
    }

    return bg as string;
  }, [widget?.widgetDefinition?.styles?.background]);

  const getActiveContextForProjectConversations = useCallback(
    (pc: ProjectConversation) => {
      return {
        id: pc.conversationId,
        type: 'PROJECT_CONVERSATIONS' as keyof typeof ContextType,
        name: pc.conversationName,
        desc: pc.conversationDescription,
        projectId: pc.project?.projectId,
      };
    },
    [],
  );

  const getActiveContextForEntityConversations = useCallback(
    (ec: ProjectGlobalConversation, pId: number) => {
      return {
        id: ec.entityId,
        type: ContextTypeReverse[ec.entityType],
        name: ec.entityName,
        projectId: pId,
      };
    },
    [],
  );

  const onProjectConversationClick = useCallback(
    (pc: ProjectConversation) => {
      const context = getActiveContextForProjectConversations(pc);

      setActiveContext(context);
    },
    [getActiveContextForProjectConversations, setActiveContext],
  );

  const onEntityConversationClick = useCallback(
    (ec: ProjectGlobalConversation, pId: number) => {
      setActiveContext(getActiveContextForEntityConversations(ec, pId));
    },
    [getActiveContextForEntityConversations, setActiveContext],
  );

  useEffect(() => {
    if (!activeContext && !trySettingInitialCOnv.current && !isResponsive) {
      const allChats =
        data.projectChats?.flatMap((e) => e?.projectConversations) ?? [];

      if (allChats.length > 0) {
        const chatWithUnreadMessages = allChats.find(
          (e) => e?.unreadMessages > 0,
        );

        if (chatWithUnreadMessages) {
          onProjectConversationClick(chatWithUnreadMessages);

          trySettingInitialCOnv.current = true;

          return;
        }
      }
      const allEntityChats =
        data.projectChats?.flatMap((e) =>
          e?.entityConversations?.map((x) => ({
            ...x,
            projectId: e?.project?.projectId,
          })),
        ) ?? [];

      if (allEntityChats.length > 0) {
        const chatWithUnreadMessages = allEntityChats.find(
          (e) => e?.unreadMessages > 0,
        );

        if (chatWithUnreadMessages) {
          onEntityConversationClick(
            chatWithUnreadMessages,
            chatWithUnreadMessages?.projectId,
          );
          trySettingInitialCOnv.current = true;

          return;
        }
      }

      if (allChats.length > 0) {
        onProjectConversationClick(allChats[0]);
        trySettingInitialCOnv.current = true;

        return;
      }
      if (allEntityChats.length > 0) {
        onEntityConversationClick(
          allEntityChats[0],
          allEntityChats[0]?.projectId,
        );
        trySettingInitialCOnv.current = true;
      }
    }
  }, [
    activeContext,
    data.projectChats,
    isResponsive,
    onEntityConversationClick,
    onProjectConversationClick,
  ]);

  useProjectGlobalConversationQuery([
    'project_id',
    activeContext?.projectId as number,
  ]);

  return (
    <CpChatPageWidgetWrapper
      background={background}
      isDisabled={disabled}
      className="!cp-max-w-none"
    >
      <>
        {isResponsive ? (
          <CpChatForMobile
            widget={widget}
            activeContext={activeContext}
            setActiveContext={setActiveContext}
            data={data}
            onProjectConversationClick={onProjectConversationClick}
            setCreateNewConversationInProject={
              setCreateNewConversationInProject
            }
            onEntityConversationClick={onEntityConversationClick}
            onFilterChange={(args) => onFilterChange?.(args)}
          />
        ) : (
          <CpChatForLaptop
            widget={widget}
            activeContext={activeContext}
            setActiveContext={setActiveContext}
            data={data}
            onProjectConversationClick={onProjectConversationClick}
            setCreateNewConversationInProject={
              setCreateNewConversationInProject
            }
            onEntityConversationClick={onEntityConversationClick}
            onFilterChange={(args) => {
              onFilterChange?.(args);
              if (activeContext) {
                queryClient.refetchQueries(getConversationsKey(activeContext));
              }
            }}
          />
        )}
      </>
      <CpCreateNewConversationModal
        isVisible={createNewConversationInProject !== undefined}
        onCancel={() => setCreateNewConversationInProject(undefined)}
        onSubmit={(name, desc) => {
          setCreateNewConversationInProject(undefined);

          if (createNewConversationInProject) {
            const { entityId, entityType } = createNewConversationInProject;

            if (entityId && entityType) {
              switch (entityType) {
                case 'PROJECT_CONVERSATIONS':
                  ProjectConversationsAPI.updateProjectConversation(
                    ['project_id', createNewConversationInProject?.projectId],
                    ['project_conversation_id', entityId],
                    {
                      conversationName: name,
                      conversationDescription: desc,
                    },
                  ).then(() => {
                    onFilterChange?.({});
                  });
                  break;

                case 'TASK':
                  break;

                default:
                  break;
              }
            } else if (!entityId && !entityType) {
              ProjectConversationsAPI.createProjectConversation(
                ['project_id', createNewConversationInProject?.projectId],
                {
                  conversationName: name,
                  conversationDescription: desc,
                  addAllMembersToChannel: false,
                  private: false,
                  project: {
                    projectId: createNewConversationInProject?.projectId,
                  },
                },
              ).then(() => {
                onFilterChange?.({});
              });
            }
          }
        }}
        isUpdateMode={createNewConversationInProject?.entityId !== undefined}
        initialData={{
          desc: createNewConversationInProject?.desc ?? '',
          name: createNewConversationInProject?.name ?? '',
        }}
      />
    </CpChatPageWidgetWrapper>
  );
};
